// Hero-stat card model — PURE. Maps a display-currency-scoped ReportingSummary
// (+ the previous period for the delta) into the SELECTED metric cards, drawn
// from the shared chart catalogue (trendSeries.CHART_METRICS). Each card's
// headline value + delta come from the whole-range summed totals via the metric's
// value() — the SAME function the charts use per bucket, so card and chart agree.
// Currency is global (scoped), so every value is a single line.

import type { ReportingSummary } from '../../types/reporting';
import {
  CHART_METRICS,
  formatMetricValue,
  sumAllTotals,
  type MetricTone,
  type TrendTotals,
} from './trendSeries';

export type DeltaDirection = 'up' | 'down' | 'flat';
export interface HeroDelta {
  pct: number; // signed % change vs the previous equivalent period
  direction: DeltaDirection;
}

export interface HeroCard {
  key: string;
  label: string;
  valueLine: string; // single formatted value (currency is global)
  tone: MetricTone;
  delta: HeroDelta | null; // null when there's no previous period
}

/**
 * The vs-previous-period change. ONE definition, shared — the hero cards here,
 * and (via the Google adapter) the Google dashboard's widget scorecards, so the
 * two dashboards can never disagree about what "+12%" means or when a change
 * counts as flat.
 *
 * `prev === undefined` means there is no comparable previous figure, which is
 * NOT the same as "it was zero" — it yields no delta at all rather than a
 * fabricated +100%.
 */
export function computeDelta(cur: number, prev: number | undefined): HeroDelta | null {
  if (prev === undefined) return null;
  if (prev === 0) return cur === 0 ? { pct: 0, direction: 'flat' } : { pct: 100, direction: 'up' };
  const pct = ((cur - prev) / prev) * 100;
  const direction: DeltaDirection = Math.abs(pct) < 0.05 ? 'flat' : pct > 0 ? 'up' : 'down';
  return { pct: Math.round(pct * 10) / 10, direction };
}

// Build the cards for `selectedKeys` (catalogue order preserved). `prev` is the
// previous-period scoped summary for the vs-previous delta (omit → no delta).
export function buildHeroCards(
  summary: ReportingSummary,
  prev: ReportingSummary | undefined,
  selectedKeys: readonly string[],
): HeroCard[] {
  const currency = summary.currencies[0] ?? null;
  const curTotals: TrendTotals = sumAllTotals(summary.trend ?? []);
  const prevTotals: TrendTotals | undefined = prev ? sumAllTotals(prev.trend ?? []) : undefined;
  const wanted = new Set(selectedKeys);

  return CHART_METRICS.filter((m) => wanted.has(m.key)).map((m) => {
    const value = m.value(curTotals);
    const prevValue = prevTotals ? m.value(prevTotals) : undefined;
    return {
      key: m.key,
      label: m.label,
      valueLine: formatMetricValue(m.format, value, currency),
      tone: m.tone,
      delta: computeDelta(value, prevValue),
    };
  });
}

// Green when the change is in the "good" direction, red when bad, slate when
// neutral/flat. Used by the delta chip.
export function deltaColorClass(tone: MetricTone, direction: DeltaDirection): string {
  if (direction === 'flat' || tone === 'neutral') return 'text-slate-400';
  const good = (tone === 'good-up' && direction === 'up') || (tone === 'good-down' && direction === 'down');
  return good ? 'text-kyck-600' : 'text-red-600';
}
